import { useState } from "react"
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  Video,
  Album as AlbumIcon,
  Layers,
  ListChecks,
  Mic,
  UserPlus,
  MessageSquare,
  MessagesSquare,
  Tag,
  Folder,
  Mail,
  FileText,
  ShieldCheck,
  Settings,
  Menu,
  LogOut,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/AuthContext"
import { useNavigate } from "react-router-dom"

/**
 * AppShell — sidebar + topbar + outlet for routed pages.
 *
 * IA decisions (M7-redesign Batch A):
 *   - Sidebar groups items by domain (Content / Users / System) instead of
 *     dumping everything flat. Old admin had 14 flat items; new has 3 sections
 *     with clear hierarchy. Addresses the "navigation cluttered" pain point.
 *   - Sticky topbar shows the current page name + a placeholder for global
 *     actions (search, notifications, user menu). Frees the sidebar from
 *     being the only place wayfinding happens.
 *   - Mobile collapse: sidebar slides over content under 768px. Internal
 *     admins are usually on desktop but tablet support is free.
 *   - Active-route highlight uses NavLink's built-in `isActive` rather than
 *     comparing pathname strings (less brittle when routes nest later).
 */
type NavItem = {
  label: string
  to: string
  icon: React.ComponentType<{ className?: string }>
}
type NavSection = { label: string; items: NavItem[] }

const NAV: NavSection[] = [
  {
    label: "Overview",
    items: [{ label: "Dashboard", to: "/dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Content",
    items: [
      { label: "Videos", to: "/videos", icon: Video },
      { label: "Albums", to: "/albums", icon: AlbumIcon },
      { label: "Chapters", to: "/chapters", icon: Layers },
      { label: "Creators", to: "/coaches", icon: Mic },
      { label: "Creator Applications", to: "/creator-applications", icon: UserPlus },
      { label: "Messages", to: "/messages", icon: MessageSquare },
      { label: "Questions", to: "/questions", icon: MessagesSquare },
      { label: "Categories", to: "/categories", icon: Folder },
      { label: "Purposes", to: "/purposes", icon: Tag },
    ],
  },
  {
    label: "People",
    items: [
      { label: "Users", to: "/users", icon: Users },
      { label: "Reports", to: "/reports", icon: ListChecks },
      { label: "Contact Inbox", to: "/inbox", icon: Mail },
    ],
  },
  {
    label: "System",
    items: [
      { label: "Subscriptions", to: "/subscriptions", icon: Settings },
      { label: "Terms & Privacy", to: "/legal", icon: FileText },
      { label: "Security", to: "/security", icon: ShieldCheck },
    ],
  },
]

function pageTitleFromPath(pathname: string): string {
  const flat = NAV.flatMap((s) => s.items)
  return flat.find((i) => pathname.startsWith(i.to))?.label ?? "Admin"
}

export default function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)
  const title = pageTitleFromPath(location.pathname)

  // Sign-out goes through AuthContext so AuthProvider state, localStorage,
  // and other tabs (via the storage event) all stay in sync.
  const handleSignOut = () => {
    signOut()
    navigate("/login", { replace: true })
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — flex column so the nav can scroll independently when the
          viewport is too short to show every entry. Without this, the bottom
          of the SYSTEM section (Terms & Privacy / Security) was getting
          clipped on laptop-sized screens with no scroll affordance. */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r bg-card transition-transform md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="flex h-16 shrink-0 items-center border-b px-6">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground font-bold">
              GT
            </div>
            <span className="font-semibold">Good Talk Admin</span>
          </Link>
        </div>

        <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-4">
          {NAV.map((section) => (
            <div key={section.label}>
              <div className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section.label}
              </div>
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                            isActive
                              ? "bg-secondary text-secondary-foreground font-medium"
                              : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                          )
                        }
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </NavLink>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div className="md:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-background/80 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle navigation"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Placeholder for future global search / notifications. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </header>

        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
